import {type ReactNode, useEffect} from "react";
import {Controller, useForm} from "react-hook-form";
import {PhoneHQButton, FieldError, PhoneHQLoader, PhoneHQDropdown, PhoneHQInput, PhoneHQSwitch} from 'ch3-ui-lib';
import {Check, ChevronDown} from "lucide-react";
import {
    useGetEnsConfigurationQuery,
    useUpdateEnsConfigurationMutation,
} from "../../../store/features/ens/ensService";
import {EnsConfiguration} from "../../../store/features/ens/types";
import {
    DTMF_KEYS,
    ENS_SETTINGS_LIMITS,
    toUpdateEnsConfigurationRequest,
    validateIntegerRange,
    validateSmsConfirmationKeywords,
} from "./ensSettingsValidation";

interface FormValues {
    maxActiveAlertsPerUser: number;
    alertHistoryRetentionDays: number;
    finalLevelTimeoutSeconds: number;
    smsConfirmationKeywordsCsv: string;
    dtmfConfirmationKey: string;
    adminOverrideAuditEnabled: boolean;
}

const fromConfig = (config: EnsConfiguration): FormValues => ({
    maxActiveAlertsPerUser: config.maxActiveAlertsPerUser,
    alertHistoryRetentionDays: config.alertHistoryRetentionDays,
    finalLevelTimeoutSeconds: config.finalLevelTimeoutSeconds,
    smsConfirmationKeywordsCsv: config.smsConfirmationKeywords.join(', '),
    dtmfConfirmationKey: config.dtmfConfirmationKey,
    adminOverrideAuditEnabled: config.adminOverrideAuditEnabled,
});

const NumberField = ({id, value, onChange, error}: {
    id: string;
    value: number;
    onChange: (value: number) => void;
    error?: string;
}) => (
    <div className="w-full">
        <PhoneHQInput
            id={id}
            type="number"
            value={String(value)}
            aria-invalid={Boolean(error)}
            onChange={(e) => onChange(Number(e.target.value))}
            className="!ml-auto !w-32 rounded-lg text-right font-semibold max-md:!ml-0"
        />
        <FieldError error={error} className="text-right"/>
    </div>
);

const TextField = ({id, value, onChange, error}: {
    id: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
}) => (
    <div className="w-full">
        <PhoneHQInput
            id={id}
            value={value}
            aria-invalid={Boolean(error)}
            onChange={(e) => onChange(e.target.value)}
            className="!w-full rounded-lg"
        />
        <FieldError error={error}/>
    </div>
);

const SettingsRow = ({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: ReactNode;
}) => (
    <div className="flex items-start justify-between gap-6 border-b border-line py-4 max-md:flex-col max-md:gap-3">
        <div className="min-w-0">
            <div className="mb-1 text-sm font-semibold text-ink">{title}</div>
            <div className="max-w-xl text-sm leading-5 text-ink-muted">{description}</div>
        </div>
        <div className="flex w-[260px] shrink-0 justify-end max-md:w-full max-md:justify-start">{children}</div>
    </div>
);

export const EnsSettings = () => {
    const {data: config, isLoading} = useGetEnsConfigurationQuery();
    const [updateConfig, {isLoading: isSaving}] = useUpdateEnsConfigurationMutation();

    const {
        control,
        handleSubmit,
        reset,
        formState: {errors, isDirty},
    } = useForm<FormValues>({
        mode: 'onBlur',
        defaultValues: {
            maxActiveAlertsPerUser: 5,
            alertHistoryRetentionDays: 365,
            finalLevelTimeoutSeconds: 600,
            smsConfirmationKeywordsCsv: '',
            dtmfConfirmationKey: '1',
            adminOverrideAuditEnabled: true,
        },
    });

    useEffect(() => {
        if (config) {
            reset(fromConfig(config));
        }
    }, [config, reset]);

    const onSubmit = async (values: FormValues) => {
        try {
            const next = await updateConfig(toUpdateEnsConfigurationRequest(values)).unwrap();
            reset(fromConfig(next));
        } catch {
            // toast handled in service
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <PhoneHQLoader/>
            </div>
        );
    }

    return (
        <form className="max-w-4xl py-6 px-5" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-2">
                <h3 className="text-base font-semibold text-ink">ENS Configuration</h3>
                <p className="text-sm text-ink-muted">
                    Global defaults and system settings for the Emergency Notification System.
                </p>
            </div>
            <Controller
                name="maxActiveAlertsPerUser"
                control={control}
                rules={{
                    required: 'Required',
                    validate: (value) => validateIntegerRange(
                        value,
                        ENS_SETTINGS_LIMITS.maxActiveAlertsPerUser.min,
                        ENS_SETTINGS_LIMITS.maxActiveAlertsPerUser.max,
                    ),
                }}
                render={({field}) => (
                    <SettingsRow
                        title="Max active alerts per user"
                        description="Maximum number of simultaneous active alerts a single user can receive. Allowed range: 1-10."
                    >
                        <NumberField
                            id="ensSettings-maxActiveAlerts"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.maxActiveAlertsPerUser?.message}
                        />
                    </SettingsRow>
                )}
            />
            <Controller
                name="alertHistoryRetentionDays"
                control={control}
                rules={{
                    required: 'Required',
                    validate: (value) => validateIntegerRange(
                        value,
                        ENS_SETTINGS_LIMITS.alertHistoryRetentionDays.min,
                        ENS_SETTINGS_LIMITS.alertHistoryRetentionDays.max,
                    ),
                }}
                render={({field}) => (
                    <SettingsRow
                        title="Alert history retention"
                        description="How long alert records are retained before archiving. Allowed range: 30-1095 days."
                    >
                        <NumberField
                            id="ensSettings-retentionDays"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.alertHistoryRetentionDays?.message}
                        />
                    </SettingsRow>
                )}
            />
            <Controller
                name="finalLevelTimeoutSeconds"
                control={control}
                rules={{
                    required: 'Required',
                    validate: (value) => validateIntegerRange(
                        value,
                        ENS_SETTINGS_LIMITS.finalLevelTimeoutSeconds.min,
                        ENS_SETTINGS_LIMITS.finalLevelTimeoutSeconds.max,
                    ),
                }}
                render={({field}) => (
                    <SettingsRow
                        title="Final level timeout"
                        description="How long the final escalation level remains active before the alert expires. Allowed range: 60-3600 seconds."
                    >
                        <NumberField
                            id="ensSettings-finalTimeout"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.finalLevelTimeoutSeconds?.message}
                        />
                    </SettingsRow>
                )}
            />
            <Controller
                name="smsConfirmationKeywordsCsv"
                control={control}
                rules={{validate: validateSmsConfirmationKeywords}}
                render={({field}) => (
                    <SettingsRow
                        title="SMS confirmation keywords"
                        description="Comma-separated keywords accepted as valid SMS confirmation replies. Use 1-10 keywords, A-Z/0-9 only, max 20 characters each."
                    >
                        <TextField
                            id="ensSettings-smsKeywords"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.smsConfirmationKeywordsCsv?.message}
                        />
                    </SettingsRow>
                )}
            />
            <Controller
                name="dtmfConfirmationKey"
                control={control}
                rules={{
                    required: 'Required',
                    pattern: {value: /^[0-9*#]$/, message: 'Single 0-9, * or # character'},
                }}
                render={({field}) => (
                    <SettingsRow
                        title="Phone DTMF confirmation key"
                        description="Which keypad digit confirms receipt during a phone call."
                    >
                        <PhoneHQDropdown
                            align="end"
                            sideOffset={4}
                            trigger={
                                <div role="button" tabIndex={0} className="relative w-[160px] cursor-pointer">
                                    <PhoneHQInput
                                        readOnly
                                        tabIndex={-1}
                                        value={field.value}
                                        aria-label="DTMF confirmation key"
                                        className="pointer-events-none w-full cursor-pointer rounded-md pr-8"
                                    />
                                    <ChevronDown
                                        size={16}
                                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
                                    />
                                </div>
                            }
                        >
                            {DTMF_KEYS.map((key) => (
                                <PhoneHQDropdown.Item key={key} onSelect={() => field.onChange(key)}>
                                    <span className="flex w-full items-center justify-between gap-4">
                                        {key}
                                        {field.value === key && <Check size={14}/>}
                                    </span>
                                </PhoneHQDropdown.Item>
                            ))}
                        </PhoneHQDropdown>
                    </SettingsRow>
                )}
            />
            <Controller
                name="adminOverrideAuditEnabled"
                control={control}
                render={({field}) => (
                    <SettingsRow
                        title="Admin override audit"
                        description="All manual override confirmations are logged when this is enabled."
                    >
                        <PhoneHQSwitch
                            checked={field.value}
                            onCheckedChange={() => field.onChange(!field.value)}
                        />
                    </SettingsRow>
                )}
            />
            <div className="flex justify-end pt-2">
                <PhoneHQButton size="sm" type="submit" disabled={!isDirty || isSaving} phq-id="ensSettings-saveBtn">{isSaving ? 'Saving…' : 'Save changes'}</PhoneHQButton>
            </div>
        </form>
    );
};
